import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const CART_STORAGE_KEY = 'ecommerce_cart';

const loadCartFromStorage = () => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
  }
  return { items: [] };
};

const saveCartToStorage = (cart) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => loadCartFromStorage());
  const [loading] = useState(false);
  const [itemCount, setItemCount] = useState(0);

  // Persistir carrito en localStorage cuando cambie
  useEffect(() => {
    saveCartToStorage(cart);
  }, [cart]);

  // Actualizar contador de items
  useEffect(() => {
    if (cart && cart.items) {
      const count = cart.items.reduce((sum, item) => sum + item.quantity, 0);
      setItemCount(count);
    } else {
      setItemCount(0);
    }
  }, [cart]);

  const addToCart = (product, quantity = 1, size = null, color = null) => {
    setCart(prev => {
      const items = [...(prev.items || [])];
      const existingIndex = items.findIndex(
        i => i.productId === product.id && i.size === size && i.color === color
      );

      if (existingIndex >= 0) {
        items[existingIndex] = {
          ...items[existingIndex],
          quantity: items[existingIndex].quantity + quantity
        };
      } else {
        items.push({
          productId: product.id,
          quantity,
          size,
          color,
          product: {
            id: product.id,
            name: product.name,
            price: product.discount > 0
              ? product.price * (1 - product.discount / 100)
              : product.price,
            originalPrice: product.price,
            discount: product.discount || 0,
            images: product.images,
            stock: product.stock
          }
        });
      }

      return { items };
    });
    return true;
  };

  const updateCartItem = (productId, quantity, size = null, color = null) => {
    setCart(prev => {
      const items = (prev.items || []).map(item => {
        if (item.productId === productId && item.size === size && item.color === color) {
          return { ...item, quantity };
        }
        return item;
      });
      return { items };
    });
    return true;
  };

  const removeFromCart = (productId, size = null, color = null) => {
    setCart(prev => {
      const items = (prev.items || []).filter(
        item => !(item.productId === productId && item.size === size && item.color === color)
      );
      return { items };
    });
    return true;
  };

  const clearCart = () => {
    setCart({ items: [] });
    return true;
  };

  const getCartTotal = () => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((sum, item) => {
      return sum + ((item.product?.price || 0) * item.quantity);
    }, 0);
  };

  const value = {
    cart,
    loading,
    itemCount,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    getCartTotal
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
